import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { transfersAPI, posAPI, warehousesAPI, brandsAPI, categoriesAPI, inventoryAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Package, Plus, Search, Send, Clock, CheckCircle, XCircle, 
  AlertTriangle, Eye, Edit, Trash2, RefreshCw, ShoppingCart,
  FileText, Truck, Building2, Printer, CheckSquare, Boxes
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import { Checkbox } from '@/components/ui/checkbox';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: Send },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  partially_approved: { label: 'Partial', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle }
};

const priorityConfig = {
  normal: { label: 'Normal', color: 'bg-gray-100 text-gray-600' },
  urgent: { label: 'Urgent', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700' }
};

const requestTypeConfig = {
  low_stock: { label: 'Low Stock', description: 'Stock below reorder level' },
  out_of_stock: { label: 'Out of Stock', description: 'No stock available' },
  new_order: { label: 'New Order', description: 'Regular replenishment' },
  emergency: { label: 'Emergency', description: 'Urgent requirement' },
  anticipated_demand: { label: 'Anticipated Demand', description: 'Expected high demand' }
};

const StoreStockRequests = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const storeId = user?.entity_id;
  
  // Main state
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Create/Edit dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [requestType, setRequestType] = useState('low_stock');
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  const [requestItems, setRequestItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ sent: 0, total: 0, phase: '' });  // live progress for huge submits
  const [successInfo, setSuccessInfo] = useState(null);  // { number, items, submitted } once a request is saved
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Browse Warehouse mode -- lets the cashier pick a warehouse, filter
  // its stock by brand/category and bulk-select products to request.
  const [addMode, setAddMode] = useState('search'); // 'search' | 'browse'
  const [warehouses, setWarehouses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [browseWarehouseId, setBrowseWarehouseId] = useState('');
  const [browseBrand, setBrowseBrand] = useState('all');
  const [browseCategory, setBrowseCategory] = useState('all');
  const [browseSearch, setBrowseSearch] = useState('');
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseItems, setBrowseItems] = useState([]);
  const [browseSelected, setBrowseSelected] = useState(() => new Set());
  
  // Detail dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: 20,
        requester_id: storeId
      };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await transfersAPI.listRequests(params);
      setRequests(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast({ title: 'Error', description: 'Failed to fetch requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, page, statusFilter, toast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Print stock request
  const printStockRequest = (request) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Stock Request - ${request.request_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header h2 { margin: 10px 0 0; font-size: 18px; color: #666; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .info-box { padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .info-box h4 { margin: 0 0 10px; font-size: 12px; color: #888; text-transform: uppercase; }
            .info-box p { margin: 0; font-size: 14px; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
            .status-submitted { background: #dbeafe; color: #1d4ed8; }
            .status-approved { background: #d1fae5; color: #047857; }
            .status-rejected { background: #fee2e2; color: #dc2626; }
            .priority-urgent { background: #fed7aa; color: #c2410c; }
            .priority-critical { background: #fecaca; color: #dc2626; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #f3f4f6; font-weight: 600; }
            .text-right { text-align: right; }
            .totals { margin-top: 20px; }
            .totals-row { display: flex; justify-content: flex-end; padding: 5px 0; }
            .totals-label { width: 150px; color: #666; }
            .totals-value { width: 100px; text-align: right; font-weight: 600; }
            .signature-section { margin-top: 50px; display: flex; justify-content: space-between; }
            .signature-box { text-align: center; width: 200px; }
            .signature-line { border-top: 1px solid #333; margin-top: 50px; padding-top: 10px; }
            .notes-box { background: #f9fafb; padding: 15px; border-radius: 8px; margin: 20px 0; }
            .notes-box h4 { margin: 0 0 10px; font-size: 12px; color: #888; }
            @media print { 
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>STOCK REQUEST</h1>
            <h2>${request.request_number}</h2>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <h4>Request From</h4>
              <p><strong>${request.requester_name}</strong></p>
              <p>${request.requester_code || ''}</p>
            </div>
            <div class="info-box">
              <h4>Request Details</h4>
              <p>Status: <span class="status-badge status-${request.status}">${request.status?.toUpperCase()}</span></p>
              <p>Priority: <span class="status-badge ${request.priority === 'urgent' ? 'priority-urgent' : request.priority === 'critical' ? 'priority-critical' : ''}">${request.priority?.toUpperCase()}</span></p>
              <p>Type: ${request.request_type?.replace('_', ' ')?.toUpperCase()}</p>
            </div>
            <div class="info-box">
              <h4>Date Information</h4>
              <p>Created: ${new Date(request.created_at).toLocaleDateString('en-IN')}</p>
              <p>Required By: ${request.required_by_date ? new Date(request.required_by_date).toLocaleDateString('en-IN') : 'Not specified'}</p>
            </div>
            <div class="info-box">
              <h4>Created By</h4>
              <p>${request.created_by_name || '-'}</p>
            </div>
          </div>
          
          ${request.notes ? `
          <div class="notes-box">
            <h4>REQUEST NOTES</h4>
            <p>${request.notes}</p>
          </div>
          ` : ''}
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>SKU</th>
                <th class="text-right">Current Stock</th>
                <th class="text-right">Requested Qty</th>
                <th class="text-right">Approved Qty</th>
              </tr>
            </thead>
            <tbody>
              ${request.items?.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.product_name}</td>
                  <td>${item.sku || item.product_sku || '-'}</td>
                  <td class="text-right">${item.current_stock || 0}</td>
                  <td class="text-right">${item.requested_qty}</td>
                  <td class="text-right">${item.approved_qty ?? '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals">
            <div class="totals-row">
              <span class="totals-label">Total Items:</span>
              <span class="totals-value">${request.items?.length || 0}</span>
            </div>
            <div class="totals-row">
              <span class="totals-label">Total Requested:</span>
              <span class="totals-value">${request.items?.reduce((sum, i) => sum + (i.requested_qty || 0), 0)}</span>
            </div>
            ${request.status === 'approved' || request.status === 'partially_approved' ? `
            <div class="totals-row">
              <span class="totals-label">Total Approved:</span>
              <span class="totals-value">${request.items?.reduce((sum, i) => sum + (i.approved_qty || 0), 0)}</span>
            </div>
            ` : ''}
          </div>
          
          ${request.review_notes ? `
          <div class="notes-box">
            <h4>${request.status === 'rejected' ? 'REJECTION REASON' : 'APPROVAL NOTES'}</h4>
            <p>${request.review_notes}</p>
          </div>
          ` : ''}
          
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">Requested By</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Approved By</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Received By</div>
            </div>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2 || !storeId) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await posAPI.searchProducts(query, storeId);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(debounce);
  }, [productSearch, searchProducts]);

  // Add product to request
  const addProductToRequest = (product) => {
    // Check if already added
    if (requestItems.some(item => item.product_id === product.id)) {
      toast({ title: 'Already Added', description: 'Product is already in the request', variant: 'destructive' });
      return;
    }
    
    setRequestItems([...requestItems, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      hsn_code: product.hsn_code,
      uom: product.uom_name || 'PCS',
      current_stock: product.store_stock || 0,
      reorder_level: product.reorder_level || 0,
      requested_qty: Math.max(1, (product.reorder_level || 10) - (product.store_stock || 0)),
      notes: ''
    }]);
    
    setProductSearch('');
    setSearchResults([]);
  };

  // Update item quantity
  const updateItemQty = (productId, qty) => {
    setRequestItems(items => items.map(item => 
      item.product_id === productId 
        ? { ...item, requested_qty: Math.max(1, parseFloat(qty) || 0) }
        : item
    ));
  };

  // Update item notes
  const updateItemNotes = (productId, notes) => {
    setRequestItems(items => items.map(item => 
      item.product_id === productId 
        ? { ...item, notes }
        : item
    ));
  };

  // Remove item
  const removeItem = (productId) => {
    setRequestItems(items => items.filter(item => item.product_id !== productId));
  };

  // ---------------------------------------------------------------------
  // Browse Warehouse — load dropdowns + stock list
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!showCreateDialog) return;
    (async () => {
      try {
        const [whRes, brRes, catRes] = await Promise.all([
          warehousesAPI.list({ is_active: true }),
          brandsAPI.list({ is_active: true }),
          categoriesAPI.list({ is_active: true }),
        ]);
        const whs = whRes.data?.items || whRes.data?.warehouses || whRes.data || [];
        setWarehouses(whs);
        if (whs.length && !browseWarehouseId) setBrowseWarehouseId(whs[0].id);
        setBrands(brRes.data?.items || brRes.data || []);
        setCategories(catRes.data?.items || catRes.data || []);
      } catch (err) {
        console.error('Failed to load browse dropdowns', err);
      }
    })();
  }, [showCreateDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrowseStock = useCallback(async () => {
    if (!browseWarehouseId || addMode !== 'browse') return;
    setBrowseLoading(true);
    try {
      const params = { view: 'active', group_by: 'product' };
      if (browseBrand !== 'all') params.brand_id = browseBrand;
      if (browseCategory !== 'all') params.category_id = browseCategory;
      if (browseSearch) params.search = browseSearch;
      const res = await inventoryAPI.getWarehouseStock(browseWarehouseId, params);
      setBrowseItems(res.data?.items || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load warehouse stock', variant: 'destructive' });
      setBrowseItems([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [browseWarehouseId, addMode, browseBrand, browseCategory, browseSearch, toast]);

  useEffect(() => {
    const t = setTimeout(fetchBrowseStock, 300);
    return () => clearTimeout(t);
  }, [fetchBrowseStock]);

  // Mark which browse items are already in the request cart (de-dup helper)
  const inCart = useMemo(() => new Set(requestItems.map(i => i.product_id)), [requestItems]);

  const toggleBrowseSelect = (productId) => {
    setBrowseSelected(prev => {
      const n = new Set(prev);
      if (n.has(productId)) n.delete(productId); else n.add(productId);
      return n;
    });
  };

  const selectAllOnPage = () => {
    const allIds = browseItems.filter(it => !inCart.has(it.product_id)).map(it => it.product_id);
    const allSelected = allIds.length > 0 && allIds.every(id => browseSelected.has(id));
    if (allSelected) {
      setBrowseSelected(new Set());
    } else {
      setBrowseSelected(new Set(allIds));
    }
  };

  // Add currently-ticked browse items into the request cart.
  // qtyMode='available' pre-fills with full warehouse quantity.
  // qtyMode='reorder'   pre-fills with max(1, reorder - store_stock).
  const addSelectedToCart = (qtyMode = 'available') => {
    const picked = browseItems.filter(it => browseSelected.has(it.product_id) && !inCart.has(it.product_id));
    if (picked.length === 0) {
      toast({ title: 'Nothing to add', description: 'Tick at least one product first' });
      return;
    }
    const additions = picked.map(it => {
      const available = Number(it.quantity) || 0;
      const reorder = Number(it.reorder_level) || 0;
      const qty = qtyMode === 'reorder'
        ? Math.max(1, reorder - 0)
        : Math.max(1, available);
      return {
        product_id: it.product_id,
        product_name: it.product_name,
        sku: it.product_sku || it.sku || '',
        hsn_code: it.hsn_code || '',
        uom: it.uom_name || it.uom || 'PCS',
        current_stock: 0,
        reorder_level: reorder,
        warehouse_available: available,
        requested_qty: qty,
        notes: '',
      };
    });
    setRequestItems(items => [...items, ...additions]);
    setBrowseSelected(new Set());
    toast({ title: 'Added', description: `${additions.length} product${additions.length > 1 ? 's' : ''} added to the request` });
  };

  // Add a single browse row straight into the cart (bypass the checkbox).
  const addSingleBrowseToCart = (it) => {
    if (inCart.has(it.product_id)) return;
    setRequestItems(items => [...items, {
      product_id: it.product_id,
      product_name: it.product_name,
      sku: it.product_sku || it.sku || '',
      hsn_code: it.hsn_code || '',
      uom: it.uom_name || it.uom || 'PCS',
      current_stock: 0,
      reorder_level: Number(it.reorder_level) || 0,
      warehouse_available: Number(it.quantity) || 0,
      requested_qty: Math.max(1, Number(it.quantity) || 1),
      notes: '',
    }]);
  };

  // One-click: request every available product (ignores ticks).
  const requestAllAvailable = () => {
    const candidates = browseItems.filter(it => !inCart.has(it.product_id) && (Number(it.quantity) || 0) > 0);
    if (candidates.length === 0) {
      toast({ title: 'Nothing to request', description: 'No new products with available stock' });
      return;
    }
    const additions = candidates.map(it => ({
      product_id: it.product_id,
      product_name: it.product_name,
      sku: it.product_sku || it.sku || '',
      hsn_code: it.hsn_code || '',
      uom: it.uom_name || it.uom || 'PCS',
      current_stock: 0,
      reorder_level: Number(it.reorder_level) || 0,
      warehouse_available: Number(it.quantity) || 0,
      requested_qty: Math.max(1, Number(it.quantity) || 0),
      notes: '',
    }));
    setRequestItems(items => [...items, ...additions]);
    setBrowseSelected(new Set());
    toast({ title: 'Added', description: `${additions.length} products added with their full warehouse qty` });
  };

  // Reset form
  const resetForm = () => {
    setEditingRequest(null);
    setRequestType('low_stock');
    setPriority('normal');
    setNotes('');
    setRequestItems([]);
    setProductSearch('');
    setSearchResults([]);
    setAddMode('search');
    setBrowseBrand('all');
    setBrowseCategory('all');
    setBrowseSearch('');
    setBrowseItems([]);
    setBrowseSelected(new Set());
    setSuccessInfo(null);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  // Open edit dialog
  const openEditDialog = (request) => {
    setEditingRequest(request);
    setRequestType(request.request_type || 'low_stock');
    setPriority(request.priority || 'normal');
    setNotes(request.notes || '');
    setRequestItems(request.items || []);
    setShowCreateDialog(true);
  };

  // Save request (create or update)
  const handleSaveRequest = async (submitAfterSave = false) => {
    // If the user ticked rows under "Browse Warehouse" but forgot to click
    // "Add selected", pull those picks straight into the API payload. We
    // intentionally skip writing them into React state here - rendering a
    // table with 500-1000 live <Input> rows blanks the dialog on slow
    // devices. The user never needs to see those rows: they're submitting
    // right away.
    let effectiveItems = requestItems;
    if (requestItems.length === 0 && browseSelected.size > 0) {
      const picked = browseItems.filter(it => browseSelected.has(it.product_id) && !inCart.has(it.product_id));
      effectiveItems = picked.map(it => {
        const available = Number(it.quantity) || 0;
        return {
          product_id: it.product_id,
          product_name: it.product_name,
          sku: it.product_sku || it.sku || '',
          hsn_code: it.hsn_code || '',
          uom: it.uom_name || it.uom || 'PCS',
          current_stock: 0,
          reorder_level: Number(it.reorder_level) || 0,
          warehouse_available: available,
          requested_qty: Math.max(1, available),
          notes: '',
        };
      });
    }

    if (effectiveItems.length === 0) {
      toast({ title: 'Error', description: 'Add at least one product to the request', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    setSubmitProgress({ sent: 0, total: effectiveItems.length, phase: 'Preparing...' });
    try {
      let savedRequest;
      const BATCH = 150;  // keeps payload < ~100KB per request and never blocks the UI
      const useBatched = effectiveItems.length > BATCH;

      if (editingRequest) {
        // Edit mode - single call (edits are usually small)
        setSubmitProgress({ sent: 0, total: effectiveItems.length, phase: 'Saving changes...' });
        const response = await transfersAPI.updateRequest(editingRequest.id, {
          request_type: requestType,
          priority,
          notes,
          items: effectiveItems
        });
        savedRequest = response.data;
        setSubmitProgress({ sent: effectiveItems.length, total: effectiveItems.length, phase: 'Saved' });
        toast({ title: 'Success', description: 'Request updated successfully' });
      } else if (useBatched) {
        // Big submit - create empty draft, then stream items in batches so the
        // user sees live progress and no single request is multi-megabyte.
        setSubmitProgress({ sent: 0, total: effectiveItems.length, phase: 'Creating request...' });
        const firstBatch = effectiveItems.slice(0, BATCH);
        const createResp = await transfersAPI.createRequest({
          requester_type: 'store',
          requester_id: storeId,
          request_type: requestType,
          priority,
          notes,
          items: firstBatch,
        });
        savedRequest = createResp.data;
        let sent = firstBatch.length;
        setSubmitProgress({ sent, total: effectiveItems.length, phase: `Sending ${sent}/${effectiveItems.length}` });

        for (let i = BATCH; i < effectiveItems.length; i += BATCH) {
          const chunk = effectiveItems.slice(i, i + BATCH);
          // eslint-disable-next-line no-await-in-loop
          await transfersAPI.appendItems(savedRequest.id, chunk);
          sent += chunk.length;
          setSubmitProgress({ sent, total: effectiveItems.length, phase: `Sending ${sent}/${effectiveItems.length}` });
        }
        toast({ title: 'Success', description: `Request created with ${effectiveItems.length} items` });
      } else {
        // Small submit - single POST (fast path)
        setSubmitProgress({ sent: 0, total: effectiveItems.length, phase: 'Saving...' });
        const response = await transfersAPI.createRequest({
          requester_type: 'store',
          requester_id: storeId,
          request_type: requestType,
          priority,
          notes,
          items: effectiveItems
        });
        savedRequest = response.data;
        setSubmitProgress({ sent: effectiveItems.length, total: effectiveItems.length, phase: 'Saved' });
        toast({ title: 'Success', description: 'Request created as draft' });
      }

      // Submit if requested
      if (submitAfterSave && savedRequest) {
        setSubmitProgress({ sent: effectiveItems.length, total: effectiveItems.length, phase: 'Submitting to HQ...' });
        await transfersAPI.submitRequest(savedRequest.id);
        toast({ title: 'Submitted', description: 'Request submitted to HQ for review' });
      }

      // Keep the dialog open and show a prominent success panel so the user
      // never sees a blank/transitional state.
      setSuccessInfo({
        number: savedRequest?.request_number || savedRequest?.id || '',
        items: effectiveItems.length,
        submitted: submitAfterSave,
      });
      fetchRequests();
    } catch (error) {
      console.error('Error saving request:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save request', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
      setSubmitProgress({ sent: 0, total: 0, phase: '' });
    }
  };

  // Submit draft request
  const handleSubmitRequest = async (request) => {
    try {
      await transfersAPI.submitRequest(request.id);
      toast({ title: 'Success', description: 'Request submitted to HQ for review' });
      fetchRequests();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to submit request', 
        variant: 'destructive' 
      });
    }
  };

  // View request details
  const viewRequestDetails = async (request) => {
    try {
      const response = await transfersAPI.getRequest(request.id);
      setSelectedRequest(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch request details', variant: 'destructive' });
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = statusConfig[status];
    if (!config) return <Badge variant="outline">{status}</Badge>;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" /> {config.label}
      </Badge>
    );
  };

  // Stats
  const draftCount = requests.filter(r => r.status === 'draft').length;
  const pendingCount = requests.filter(r => ['submitted', 'under_review'].includes(r.status)).length;
  const approvedCount = requests.filter(r => ['approved', 'partially_approved'].includes(r.status)).length;

  return (
    <div className="p-6 space-y-6" data-testid="store-stock-requests-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-emerald-600" />
            Stock Requests
          </h1>
          <p className="text-gray-500">Request stock replenishment from HQ</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={fetchRequests} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
            <Plus className="w-4 h-4" /> New Request
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Drafts</p>
                <p className="text-2xl font-bold text-gray-700">{draftCount}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Pending Review</p>
                <p className="text-2xl font-bold text-blue-800">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Approved</p>
                <p className="text-2xl font-bold text-emerald-800">{approvedCount}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Total Requests</p>
                <p className="text-2xl font-bold text-purple-800">{requests.length}</p>
              </div>
              <Package className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-gray-600">Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Request #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    Loading requests...
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No stock requests found</p>
                    <p className="text-sm">Create a new request to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-gray-50">
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600 font-medium">
                        {request.request_number}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600 capitalize">
                        {request.request_type?.replace('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {request.items?.length || 0}
                    </TableCell>
                    <TableCell>
                      <Badge className={priorityConfig[request.priority]?.color || 'bg-gray-100'}>
                        {request.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(request.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewRequestDetails(request)}
                          className="text-gray-600 hover:text-blue-600"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.status === 'draft' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(request)}
                              className="text-gray-600 hover:text-amber-600"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => handleSubmitRequest(request)}
                              className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                            >
                              <Send className="w-4 h-4" /> Submit
                            </Button>
                          </>
                        )}
                        {request.transfer_order_number && (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                            <Truck className="w-3 h-3 mr-1" />
                            {request.transfer_order_number}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button 
            variant="outline" 
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create/Edit Request Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-white !flex !flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
              {editingRequest ? 'Edit Stock Request' : 'Create Stock Request'}
            </DialogTitle>
            <DialogDescription>
              Request stock replenishment from HQ
            </DialogDescription>
          </DialogHeader>

          {/* Submit progress overlay - keeps the dialog alive and tells the
              user we are saving (prevents the perception of a blank page
              when the request has lots of items and the API takes a bit). */}
          {submitting && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm" data-testid="submit-overlay">
              <div className="flex flex-col items-center gap-3 px-8 py-6 rounded-xl border border-gray-200 bg-white shadow-xl w-[380px]">
                <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                <div className="font-semibold text-gray-800 text-lg">
                  {submitProgress.phase || 'Submitting request...'}
                </div>
                {submitProgress.total > 0 && (
                  <>
                    <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all duration-200"
                        style={{ width: `${Math.min(100, Math.round((submitProgress.sent / Math.max(1, submitProgress.total)) * 100))}%` }}
                        data-testid="submit-progress-bar"
                      />
                    </div>
                    <div className="text-sm text-gray-600 font-medium">
                      {submitProgress.sent.toLocaleString('en-IN')} / {submitProgress.total.toLocaleString('en-IN')} items
                      <span className="ml-2 text-gray-400">
                        ({Math.round((submitProgress.sent / Math.max(1, submitProgress.total)) * 100)}%)
                      </span>
                    </div>
                  </>
                )}
                <div className="text-xs text-gray-500 text-center">
                  Please keep this window open until the request is confirmed.
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-6 pb-2">
          {successInfo ? (
            /* Success state - replaces the form so the user sees a clear
               confirmation instead of a silently-closing dialog. */
            <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="success-panel">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <div className="text-xl font-semibold text-gray-800 mb-1">
                {successInfo.submitted ? 'Request Submitted' : 'Draft Saved'}
              </div>
              <div className="text-sm text-gray-600 mb-4">
                {successInfo.submitted
                  ? 'Your request has been submitted to HQ for review.'
                  : 'Your draft has been saved. You can submit it later from the list.'}
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200 mb-6">
                <span className="text-xs text-gray-500">Request No.</span>
                <span className="font-mono font-semibold text-blue-600" data-testid="success-request-number">
                  {successInfo.number}
                </span>
                <span className="text-xs text-gray-500 ml-3">Items</span>
                <span className="font-semibold text-gray-800">{successInfo.items}</span>
              </div>
            </div>
          ) : (
          <div className="space-y-6">
            {/* Request Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Request Type</Label>
                <Select value={requestType} onValueChange={setRequestType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(requestTypeConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div>
                          <span className="font-medium">{config.label}</span>
                          <span className="text-xs text-gray-500 ml-2">{config.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea 
                placeholder="Add any notes or special instructions for this request..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>

            {/* Add Products - Search OR Browse Warehouse */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Add Products</Label>
                <Tabs value={addMode} onValueChange={setAddMode} className="w-auto">
                  <TabsList className="h-8">
                    <TabsTrigger value="search" className="text-xs h-7" data-testid="add-mode-search">
                      <Search className="w-3.5 h-3.5 mr-1" /> Quick Search
                    </TabsTrigger>
                    <TabsTrigger value="browse" className="text-xs h-7" data-testid="add-mode-browse">
                      <Boxes className="w-3.5 h-3.5 mr-1" /> Browse Warehouse
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {addMode === 'search' ? (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input 
                      placeholder="Search products by name, SKU, or barcode..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                      {searchResults.map(product => (
                        <div 
                          key={product.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                          onClick={() => addProductToRequest(product)}
                        >
                          <div>
                            <p className="font-medium text-gray-800">{product.name}</p>
                            <p className="text-xs text-gray-500">
                              SKU: {product.sku} • Stock: {product.store_stock || 0}
                            </p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-emerald-600">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchLoading && (
                    <p className="text-sm text-gray-500 p-2">Searching...</p>
                  )}
                </>
              ) : (
                <div className="space-y-3 border rounded-lg p-3 bg-gray-50">
                  {/* Browse filters */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <Select value={browseWarehouseId} onValueChange={setBrowseWarehouseId}>
                      <SelectTrigger data-testid="browse-warehouse-select">
                        <SelectValue placeholder="Pick a warehouse" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map(w => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.code} — {w.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={browseBrand} onValueChange={setBrowseBrand}>
                      <SelectTrigger data-testid="browse-brand-select">
                        <SelectValue placeholder="All brands" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All brands</SelectItem>
                        {brands.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select value={browseCategory} onValueChange={setBrowseCategory}>
                      <SelectTrigger data-testid="browse-category-select">
                        <SelectValue placeholder="All categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search name/SKU…"
                        value={browseSearch}
                        onChange={(e) => setBrowseSearch(e.target.value)}
                        className="pl-9"
                        data-testid="browse-search-input"
                      />
                    </div>
                  </div>

                  {/* Action strip (simplified) */}
                  <div className="flex flex-wrap gap-2 items-center bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <Button size="sm" variant="outline" onClick={selectAllOnPage} data-testid="browse-select-all-btn">
                      <CheckSquare className="w-3.5 h-3.5 mr-1" />
                      {browseItems.filter(it => !inCart.has(it.product_id)).every(it => browseSelected.has(it.product_id)) && browseItems.length > 0
                        ? 'Unselect all' : 'Select all'}
                    </Button>
                    <span className="text-sm font-medium text-emerald-800">
                      {browseSelected.size > 0
                        ? `${browseSelected.size} product${browseSelected.size === 1 ? '' : 's'} selected - will be submitted on 'Save & Submit'`
                        : `Tick rows and press Save & Submit below`}
                    </span>
                    <span className="text-xs text-gray-500 ml-auto">
                      {browseItems.length} product{browseItems.length === 1 ? '' : 's'} shown
                    </span>
                  </div>

                  {/* Browse table */}
                  <div className="border rounded-lg bg-white max-h-80 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-gray-50 sticky top-0">
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right w-24">Available</TableHead>
                          <TableHead className="text-right w-20">Batches</TableHead>
                          <TableHead className="w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {browseLoading ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">Loading…</TableCell></TableRow>
                        ) : browseItems.length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-6">No products match. Try widening filters.</TableCell></TableRow>
                        ) : browseItems.map(it => {
                          const already = inCart.has(it.product_id);
                          const checked = browseSelected.has(it.product_id);
                          return (
                            <TableRow key={it.product_id} className={already ? 'bg-emerald-50/60' : ''}>
                              <TableCell>
                                <Checkbox
                                  checked={checked}
                                  disabled={already}
                                  onCheckedChange={() => toggleBrowseSelect(it.product_id)}
                                  data-testid={`browse-row-${it.product_id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <p className="font-medium text-gray-800">{it.product_name}</p>
                                <p className="text-xs text-gray-500">{it.product_sku || it.sku}</p>
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {Number(it.quantity || 0).toLocaleString('en-IN')}
                              </TableCell>
                              <TableCell className="text-right text-gray-600">{it.batch_count || 0}</TableCell>
                              <TableCell className="text-right">
                                {already ? (
                                  <Badge variant="secondary" className="text-xs">In cart</Badge>
                                ) : (
                                  <Button size="sm" variant="ghost" className="text-emerald-600 h-7 px-2" onClick={() => addSingleBrowseToCart(it)}>
                                    <Plus className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            {/* Request Items */}
            <div className="space-y-2">
              <Label>Request Items ({requestItems.length})</Label>
              {requestItems.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500">
                  <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>No products added yet</p>
                  <p className="text-sm">Search and add products above</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right w-24">Available</TableHead>
                        <TableHead className="text-right w-32">Request Qty</TableHead>
                        <TableHead className="w-40">Notes</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requestItems.slice(0, 200).map((item) => (
                        <TableRow key={item.product_id}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            {item.warehouse_available !== undefined ? (
                              <span className="text-emerald-700 font-medium" title="Available at warehouse">
                                {Number(item.warehouse_available).toLocaleString('en-IN')}
                              </span>
                            ) : (
                              <span className={item.current_stock <= (item.reorder_level || 0) ? 'text-red-600 font-medium' : 'text-gray-600'}>
                                {item.current_stock}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Input 
                              type="number"
                              min="1"
                              value={item.requested_qty}
                              onChange={(e) => updateItemQty(item.product_id, e.target.value)}
                              className="w-24 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              placeholder="Notes..."
                              value={item.notes || ''}
                              onChange={(e) => updateItemNotes(item.product_id, e.target.value)}
                              className="text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeItem(item.product_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {requestItems.length > 200 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-3 bg-amber-50 text-amber-800 text-sm font-medium">
                            + {requestItems.length - 200} more items added (showing first 200 for performance - all {requestItems.length} will be submitted)
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
          )}
          </div>

          <DialogFooter className="gap-2 border-t border-gray-200 px-6 py-4 bg-white">
            {successInfo ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { resetForm(); setShowCreateDialog(true); }}
                  data-testid="success-create-another-btn"
                >
                  Create Another
                </Button>
                <Button
                  onClick={() => { setShowCreateDialog(false); resetForm(); }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="success-done-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={submitting}>
                  Cancel
                </Button>
            <Button 
              variant="outline"
              onClick={() => handleSaveRequest(false)}
              disabled={submitting || (requestItems.length === 0 && browseSelected.size === 0)}
              data-testid="save-draft-btn"
            >
              {submitting ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button 
              onClick={() => handleSaveRequest(true)}
              disabled={submitting || (requestItems.length === 0 && browseSelected.size === 0)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-submit-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting...' : 'Save & Submit'}
            </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Package className="w-5 h-5 text-blue-600" />
              Request Details - {selectedRequest?.request_number}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Status & Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Priority</Label>
                  <Badge className={`mt-1 ${priorityConfig[selectedRequest.priority]?.color}`}>
                    {selectedRequest.priority}
                  </Badge>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Request Type</Label>
                  <p className="font-medium text-gray-800 capitalize mt-1">
                    {selectedRequest.request_type?.replace('_', ' ')}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Created</Label>
                  <p className="font-medium text-gray-800 mt-1">
                    {formatDate(selectedRequest.created_at)}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-xs text-blue-600">Request Notes</Label>
                  <p className="text-gray-700 mt-1">{selectedRequest.notes}</p>
                </div>
              )}

              {/* HQ Review Notes */}
              {selectedRequest.review_notes && (
                <div className={`p-3 rounded-lg border ${
                  selectedRequest.status === 'rejected' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <Label className={`text-xs ${
                    selectedRequest.status === 'rejected' ? 'text-red-600' : 'text-emerald-600'
                  }`}>
                    HQ Review Notes
                  </Label>
                  <p className="text-gray-700 mt-1">{selectedRequest.review_notes}</p>
                </div>
              )}

              {/* Transfer Order Info */}
              {selectedRequest.transfer_order_number && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Label className="text-xs text-emerald-600">Transfer Order Created</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Truck className="w-4 h-4 text-emerald-600" />
                    <span className="font-mono font-medium text-emerald-700">
                      {selectedRequest.transfer_order_number}
                    </span>
                  </div>
                  {selectedRequest.source_entity_name && (
                    <p className="text-sm text-gray-600 mt-1">
                      Source: {selectedRequest.source_entity_name}
                    </p>
                  )}
                </div>
              )}

              {/* Items */}
              <div>
                <Label className="text-gray-700 font-medium">Requested Items</Label>
                <div className="border rounded-lg overflow-hidden mt-2">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Current Stock</TableHead>
                        <TableHead className="text-right">Requested</TableHead>
                        <TableHead className="text-right">Approved</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedRequest.items?.slice(0, 200).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-right text-gray-600">
                            {item.current_stock || 0}
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-800">
                            {item.requested_qty}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.approved_qty !== null && item.approved_qty !== undefined ? (
                              <span className={
                                item.approved_qty < item.requested_qty 
                                  ? 'text-amber-600 font-medium' 
                                  : 'text-emerald-600 font-medium'
                              }>
                                {item.approved_qty}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(selectedRequest.items?.length || 0) > 200 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-3 bg-amber-50 text-amber-800 text-sm font-medium">
                            + {selectedRequest.items.length - 200} more items in this request (showing first 200)
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => printStockRequest(selectedRequest)}
              className="mr-auto"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreStockRequests;
